import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../hooks/useAuth.js';
import { headerService } from '../services/headerService.js';
import { useError } from '../context/ErrorContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [headerContent, setHeaderContent] = useState(null);
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { showError } = useError();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const content = await headerService.getHeaderContent();
        setHeaderContent(content);
      } catch (error) {
        showError('Failed to load header content: ' + error.message, 'error');
      }
    };
    fetchContent();
  }, [showError]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isHome = location.pathname === '/';

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  if (!headerContent) {
    return (
      <header className="fixed w-full z-50 flex justify-center top-3 md:top-6">
        <nav
          className={`flex items-center transition-all duration-500 rounded-full ${
            isScrolled || !isHome
              ? 'bg-white/90 backdrop-blur-md shadow-lg'
              : 'bg-black/20 backdrop-blur-sm'
          }`}
        >
          
        </nav>
      </header>
    );
  }

  return (
    <>
      <header className="fixed w-full z-50 flex justify-center top-3 md:top-6">
        <nav
          className={`flex items-center transition-all duration-500 rounded-full ${
            isScrolled || !isHome
              ? 'bg-white/90 backdrop-blur-md shadow-lg'
              : 'bg-black/20 backdrop-blur-sm'
          }`}
        >
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center h-12 px-2">
            {headerContent.navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative px-4 py-2 text-sm font-medium transition-colors duration-300 rounded-full ${
                  location.pathname === item.path
                    ? isScrolled || !isHome
                      ? 'text-accent bg-accent/10'
                      : 'text-white bg-white/20'
                    : isScrolled || !isHome
                    ? 'text-text-primary hover:bg-gray-100'
                    : 'text-white hover:bg-white/20'
                }`}
              >
                {item.name}
              </Link>
            ))}
            {isAuthenticated && (
              <Link
                to={headerContent.adminDashboardLink.path}
                className="ml-2 px-4 py-2 text-sm font-medium text-accent hover:text-accent/80 transition-all duration-300 rounded-full"
              >
                {headerContent.adminDashboardLink.name}
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-3 rounded-full transition-colors ${
              isScrolled || !isHome
                ? 'text-text-primary hover:bg-gray-100'
                : 'text-white hover:bg-white/20'
            }`}
            onClick={toggleMobileMenu}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </nav>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: '0%' }}
            exit={{ x: '-100%' }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex flex-col p-8 md:hidden"
          >
            <div className="flex justify-between items-center mb-10">
              <span className="text-5xl font-bold text-white">𝓐𝓷𝓽𝓪𝓬𝓻𝓮𝓼</span> 
              <button
                onClick={toggleMobileMenu}
                className="text-white p-2 rounded-full hover:bg-white/20 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <nav className="flex flex-col space-y-4">
              {headerContent.navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative text-4xl font-bold transition-colors duration-300 ${
                    location.pathname === item.path
                      ? 'text-accent'
                      : 'text-white hover:text-accent/80'
                  }`}
                  onClick={toggleMobileMenu}
                >
                  {item.name}
                </Link>
              ))}
              {isAuthenticated && (
                <Link
                  to={headerContent.adminDashboardLink.path}
                  className="mt-6 text-2xl font-bold text-accent hover:text-accent/80 transition-colors"
                  onClick={toggleMobileMenu}
                >
                  {headerContent.adminDashboardLink.name}
                </Link>
              )}
            </nav>
            <div className="mt-auto pt-8 border-t border-gray-700">
             <Link to={headerContent.mobileCta.link}
             onClick={toggleMobileMenu}
             >
              <button className="bg-accent text-white px-6 py-3 rounded-full text-lg font-medium w-full hover:bg-accent/80 transition-colors">
                {headerContent.mobileCta.text}
              </button>
             </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;